import type { FileView, KeyView } from "@envstash/core-wasm";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangleIcon, HistoryIcon, ListIcon, PencilIcon, PlusIcon, Trash2Icon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import { Confirm } from "@/components/confirm";
import { CheckoutsCard, HeldFileCard, presentCheckouts } from "@/components/decisions";
import { MaskedValue } from "@/components/masked-value";
import { EmptyState, ErrorText, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/icon-button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { fmtDateTime, projectDisplayPath, timeAgo } from "@/lib/format";
import { useDeviceNames, useDevices, useVault } from "@/lib/hooks";
import { isGone } from "@/lib/projects";
import { vaultStore } from "@/lib/vault-store";

export const Route = createFileRoute("/_app/projects/$projectId")({ component: ProjectPage });

function ProjectPage() {
  const { projectId } = Route.useParams();
  const v = useVault();
  const deviceName = useDeviceNames();
  const devices = useDevices();
  const project = v.snapshot?.projects.find((p) => p.id === projectId);
  const files = (v.snapshot?.files ?? [])
    .filter((f) => f.project_id === projectId)
    .sort((a, b) => a.rel_path.localeCompare(b.rel_path));
  if (!v.snapshot) return null;
  if (!project) return <EmptyState title="Project not found" />;
  const checkouts = presentCheckouts(project);
  const gone = isGone(project, devices.data ? new Set(devices.data.map((d) => d.id)) : null);
  const lastAt = project.checkouts
    .filter((c) => c.status === "removed")
    .map((c) => `${c.local_path} on ${deviceName(c.device_id)}`);
  const perDevice = new Map<string, number>();
  for (const c of checkouts) perDevice.set(c.device_id, (perDevice.get(c.device_id) ?? 0) + 1);
  const showCheckouts = checkouts.some((c) => c.status !== "active") || [...perDevice.values()].some((n) => n > 1);
  const held = files.flatMap((f) => f.holds.filter((h) => h.resolution === null).map((h) => ({ file: f, hold: h })));
  return (
    <>
      <PageHeader
        title={project.name}
        description={
          <>
            <span className="font-mono">{projectDisplayPath(project.identity)}</span>
            <br />
            {project.checkouts
              .filter((c) => c.status === "active")
              .map((c) => (
                <span key={`${c.device_id}:${c.local_path}`} className="mr-3">
                  {deviceName(c.device_id)}: <span className="font-mono">{c.local_path}</span>
                </span>
              ))}
          </>
        }
      />
      {gone ? (
        <div className="mb-6 rounded-xl border bg-muted/40 p-4 text-sm" data-testid="gone-banner">
          <p className="font-medium">Not on any Mac any more.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {lastAt.length ? `Last at ${lastAt.join(", ")}. ` : ""}
            History and restore still work, and the project comes back by itself when a folder with it reappears.
          </p>
        </div>
      ) : null}
      {held.length > 0 ? (
        <div className="mb-6 space-y-3" data-testid="held-files">
          {held.map(({ file, hold }) => (
            <HeldFileCard
              key={`${file.id}:${hold.device_id}:${hold.local_path}`}
              file={file}
              hold={hold}
              projectName={project.name}
            />
          ))}
        </div>
      ) : null}
      {showCheckouts ? (
        <div className="mb-6">
          <CheckoutsCard project={project} />
        </div>
      ) : null}
      {files.length === 0 ? <EmptyState title="No tracked files in this project" /> : null}
      <div className="space-y-6">
        {files.map((f) =>
          f.kind === "env" ? <EnvFileCard key={f.id} file={f} /> : <BlobFileCard key={f.id} file={f} />,
        )}
      </div>
    </>
  );
}

function BlobFileCard({ file }: { file: FileView }) {
  const deviceName = useDeviceNames();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-mono text-sm">{file.rel_path}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <Badge variant="outline">{file.mode}</Badge>
          {file.blob_conflicts.length > 0 ? <Badge variant="destructive">conflict</Badge> : null}
          <Button size="sm" variant="outline" render={<Link to="/files/$fileId" params={{ fileId: file.id }} />}>
            <HistoryIcon /> {file.blob_history.length} version{file.blob_history.length === 1 ? "" : "s"}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {file.blob_current
          ? `Latest ${file.blob_current.content_hash.slice(0, 10)} · ${deviceName(file.blob_current.hlc.device_id)} · ${timeAgo(file.blob_current.hlc.wall_ms)}`
          : "No content yet"}
      </CardContent>
    </Card>
  );
}

function EnvFileCard({ file }: { file: FileView }) {
  const [editing, setEditing] = useState<{ key: string; value: string; isNew: boolean } | null>(null);
  const [history, setHistory] = useState<KeyView | null>(null);
  const [timeline, setTimeline] = useState(false);
  const keys = file.keys.filter((k) => k.value !== null);
  const revert = (key: string, value: string | null) => {
    const op = vaultStore.vault?.setKeyOp(file.id, key, value);
    if (op) void vaultStore.commit([op]);
  };
  const deviceName = useDeviceNames();
  const v = useVault();
  const conflicts = (v.snapshot?.conflicts ?? []).filter((c) => c.file_id === file.id && c.kind === "env_key");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-mono text-sm">{file.rel_path}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <Badge variant="outline">{file.mode}</Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setTimeline(true);
            }}
          >
            <ListIcon /> Timeline
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing({ key: "", value: "", isNew: true });
            }}
          >
            <PlusIcon /> Add key
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="p-0">
        {conflicts.map((c) =>
          c.kind === "env_key" ? (
            <div
              key={c.key}
              className="m-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm"
              data-testid="conflict"
            >
              <p className="flex items-center gap-2 font-medium">
                <AlertTriangleIcon className="size-4 text-warning" /> {c.key} was changed on two devices
              </p>
              <ul className="mt-2 space-y-1">
                {[c.winner, ...c.losers].map((ver, i) => (
                  <li key={ver.hlc.device_id + String(ver.hlc.wall_ms)} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {deviceName(ver.hlc.device_id)} · {timeAgo(ver.hlc.wall_ms)}{" "}
                      {i === 0 ? "(currently winning)" : ""}
                    </span>
                    <MaskedValue value={ver.value ?? ""} className="flex-1" />
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        const op = vaultStore.vault?.setKeyOp(file.id, c.key, ver.value);
                        if (op) void vaultStore.commit([op]);
                      }}
                    >
                      Keep this
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null,
        )}
        {keys.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No keys.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {keys.map((k) => (
                <tr key={k.key} className="border-t first:border-t-0">
                  <td className="w-[30%] max-w-0 px-4 py-2 align-middle">
                    <div className="truncate font-mono text-xs font-medium" title={k.key}>
                      {k.key}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground" title={fmtDateTime(k.hlc.wall_ms)}>
                      {deviceName(k.hlc.device_id)} · {timeAgo(k.hlc.wall_ms)}
                    </div>
                  </td>
                  <td className="max-w-0 px-2 py-2 align-middle">
                    <MaskedValue value={k.value ?? ""} />
                  </td>
                  <td className="w-0 px-2 py-2 align-middle whitespace-nowrap">
                    <IconButton
                      label={`History of ${k.key}`}
                      onClick={() => {
                        setHistory(k);
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                    <IconButton
                      label={`Edit ${k.key}`}
                      onClick={() => {
                        setEditing({ key: k.key, value: k.value ?? "", isNew: false });
                      }}
                    >
                      <PencilIcon />
                    </IconButton>
                    <Confirm
                      trigger={
                        <IconButton label={`Delete ${k.key}`}>
                          <Trash2Icon />
                        </IconButton>
                      }
                      title={`Delete ${k.key}?`}
                      description="The key is removed from the file on every device. Previous values stay in history."
                      actionLabel="Delete"
                      destructive
                      onConfirm={async () => {
                        const op = vaultStore.vault?.setKeyOp(file.id, k.key, null);
                        if (op) await vaultStore.commit([op]);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      <EditKeyDialog fileId={file.id} editing={editing} onClose={() => setEditing(null)} />
      <Dialog open={history !== null} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-mono">{history?.key}</DialogTitle>
            <DialogDescription>Every value this key has had, newest first.</DialogDescription>
          </DialogHeader>
          <ol className="max-h-[60vh] space-y-2 overflow-auto">
            {history?.history.map((h, i) => {
              const isCurrent = i === 0;
              return (
                <li
                  key={`${h.hlc.wall_ms}-${h.hlc.counter}-${h.hlc.device_id}`}
                  className="rounded-lg border p-2 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {deviceName(h.hlc.device_id)} · {fmtDateTime(h.hlc.wall_ms)}
                      {isCurrent ? " · current" : ""}
                    </p>
                    {!isCurrent ? (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          revert(history.key, h.value);
                          setHistory(null);
                        }}
                      >
                        <Undo2Icon /> Revert to this
                      </Button>
                    ) : null}
                  </div>
                  {h.value === null ? <p className="text-xs italic">deleted</p> : <MaskedValue value={h.value} />}
                </li>
              );
            })}
          </ol>
        </DialogContent>
      </Dialog>
      <Dialog open={timeline} onOpenChange={setTimeline}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono">{file.rel_path}</DialogTitle>
            <DialogDescription>
              Every change to every key in this file, newest first. Reverting writes a new version, so it syncs
              everywhere.
            </DialogDescription>
          </DialogHeader>
          <ol className="max-h-[60vh] space-y-2 overflow-auto" data-testid="timeline">
            {fileTimeline(file).map((e) => (
              <li
                key={`${e.key}-${e.hlc.wall_ms}-${e.hlc.counter}-${e.hlc.device_id}`}
                className="rounded-lg border p-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-xs text-muted-foreground">
                    <span className="font-mono font-medium text-foreground">{e.key}</span> ·{" "}
                    {deviceName(e.hlc.device_id)} · {fmtDateTime(e.hlc.wall_ms)}
                    {e.isCurrent ? " · current" : ""}
                  </p>
                  {!e.isCurrent ? (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        revert(e.key, e.value);
                      }}
                    >
                      <Undo2Icon /> Revert to this
                    </Button>
                  ) : null}
                </div>
                {e.value === null ? <p className="text-xs italic">deleted</p> : <MaskedValue value={e.value} />}
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** All key versions of an env file in one list, newest first. */
function fileTimeline(
  file: FileView,
): { key: string; value: string | null; hlc: KeyView["hlc"]; isCurrent: boolean }[] {
  const entries = file.keys.flatMap((k) =>
    k.history.map((h, i) => ({ key: k.key, value: h.value, hlc: h.hlc, isCurrent: i === 0 })),
  );
  return entries.sort(
    (a, b) =>
      b.hlc.wall_ms - a.hlc.wall_ms || b.hlc.counter - a.hlc.counter || b.hlc.device_id.localeCompare(a.hlc.device_id),
  );
}

function EditKeyDialog({
  fileId,
  editing,
  onClose,
}: {
  fileId: string;
  editing: { key: string; value: string; isNew: boolean } | null;
  onClose: () => void;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [seen, setSeen] = useState<typeof editing>(null);
  if (editing !== seen) {
    setSeen(editing);
    setKey(editing?.key ?? "");
    setValue(editing?.value ?? "");
    setError(null);
  }
  const save = async () => {
    const k = key.trim();
    if (!/^[A-Za-z_][A-Za-z0-9_.-]*$/.test(k)) {
      setError("Key must look like AN_ENV_VAR");
      return;
    }
    setBusy(true);
    try {
      const op = vaultStore.vault?.setKeyOp(fileId, k, value);
      if (op) await vaultStore.commit([op]);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing?.isNew ? "Add key" : `Edit ${editing?.key ?? ""}`}</DialogTitle>
          <DialogDescription>The change syncs to every device as soon as it is online.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="key">Key</Label>
            <Input
              id="key"
              className="font-mono"
              value={key}
              disabled={!editing?.isNew}
              onChange={(e) => setKey(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="value">Value</Label>
            <Textarea
              id="value"
              className="font-mono"
              rows={3}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <ErrorText error={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
